import React, { useState, useEffect } from "react"
import {
  Row,
  Col,
  Button,
  Label,
  Input,
  Form,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap"
import { RiUserAddLine } from "react-icons/ri"
import API from "../../api/axios"
import { toast } from "react-toastify"
import { useFormik } from "formik"
import * as Yup from "yup"
import { useDispatch, useSelector } from "react-redux"
import { fetchLeaveDays } from "../../store/clinicSetting/action"

const LeaveForm = ({ refresh }) => {
  const dispatch = useDispatch()

  const [searchTerm, setSearchTerm] = useState("")
  const [filteredUsers, setFilteredUsers] = useState([])
  const [open, setOpen] = useState(false)
  const [days_count, set_days_count] = useState(0)

  const leaveDaysList = useSelector(
    state => state.clinicSetting?.leaveDays || [],
  )

  console.log("leaveDaysList", leaveDaysList)

  const formik = useFormik({
    initialValues: {
      user_id: "",
      leave_startDate: "",
      leave_endDate: "",
      status: "enabled",
      remark: "",
    },
    validationSchema: Yup.object({
      user_id: Yup.string().required("User is required"),
      leave_startDate: Yup.date().required("Start date is required"),
      leave_endDate: Yup.date()
        .min(Yup.ref("leave_startDate"), "End date can't be before start date")
        .required("End date is required"),
      status: Yup.string().required("Status is required"),
      remark: Yup.string(),
    }),
    onSubmit: async values => {
      const getDayName = dateStr =>
        new Date(dateStr)
          .toLocaleDateString("en-US", { weekday: "long" })
          .toLowerCase()

      const startDay = getDayName(values.leave_startDate)
      const endDay = getDayName(values.leave_endDate)

      if (leaveDaysList.includes(startDay) && leaveDaysList.includes(endDay)) {
        toast.error(
          `Clinic is closed on selected date(s): ${startDay}${endDay !== startDay ? " and " + endDay : ""}`,
        )
        return
      }

      try {
        const token = localStorage.getItem("authToken")
        await API.post("/leaves", values, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })

        toast.success("Leave added successfully!", { autoClose: 2000 })
        refresh()
        handleClose()
      } catch (error) {
        console.error("Error submitting leave:", error)
        toast.error("Failed to add leave")
      }
    },
  })

  useEffect(() => {
    const { leave_startDate, leave_endDate } = formik.values

    const getWorkingDays = (startDate, endDate) => {
      const start = new Date(startDate)
      const end = new Date(endDate)
      let count = 0
      let current = new Date(start)

      while (current <= end) {
        const dayName = current
          .toLocaleDateString("en-US", { weekday: "long" })
          .toLowerCase()
        if (!leaveDaysList.includes(dayName)) {
          count++
        }
        current.setDate(current.getDate() + 1)
      }

      set_days_count(count)
    }

    if (leave_startDate && leave_endDate && leaveDaysList.length > 0) {
      getWorkingDays(leave_startDate, leave_endDate)
    } else {
      set_days_count(0)
    }
  }, [
    formik.values.leave_startDate,
    formik.values.leave_endDate,
    leaveDaysList,
  ])

  const handleClickOpen = () => {
    formik.resetForm()
    setSearchTerm("")
    setFilteredUsers([])
    set_days_count(0)
    setOpen(true)

    //  Trigger Redux-Saga to load closed days
    dispatch(fetchLeaveDays())
  }

  const handleClose = () => setOpen(false)

  const handleSearchChange = e => {
    const value = e.target.value.toLowerCase()
    setSearchTerm(value)
  }

  useEffect(() => {
    if (searchTerm.length > 2) {
      handleSearch(searchTerm)
    } else {
      setFilteredUsers([])
    }
  }, [searchTerm])

  const handleSearch = async query => {
    try {
      const response = await API.get(
        `/users/demo/search?search=${query}`,
      )
      setFilteredUsers(response.data.users || [])
    } catch (err) {
      console.error("Search error:", err)
    }
  }

  const handleUserClick = user => {
    formik.setFieldValue("user_id", user.id)
    setSearchTerm(`${user.fname} ${user.lname}`)
    setFilteredUsers([])
  }

  return (
    <div>
      <Button
        color="primary"
        onClick={handleClickOpen}
        className="float-right px-1 border py-2 mb-4 mt-2"
      >
        <RiUserAddLine size={25} /> Add Leaves
      </Button>

      <Modal isOpen={open} toggle={handleClose}>
        <ModalHeader toggle={handleClose}>Add New Leaves</ModalHeader>
        <ModalBody>
          <Form onSubmit={formik.handleSubmit}>
            <Row>
              <Col md="12" className="position-relative">
                <Label>User Name</Label>
                <Input
                  type="text"
                  name="fname"
                  placeholder="Search user"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  required
                />
                {formik.errors.user_id && formik.touched.user_id && (
                  <div className="text-danger">{formik.errors.user_id}</div>
                )}

                {filteredUsers.length > 0 && (
                  <ul className="dropdown-menu show w-100 shadow-lg bg-light rounded">
                    {filteredUsers.map(user => (
                      <li key={user.id}>
                        <button
                          className="dropdown-item"
                          type="button"
                          onClick={() => handleUserClick(user)}
                        >
                          {user.fname} {user.lname}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </Col>
            </Row>

            <Row className="mt-3">
              <Col md="6">
                <Label>Leave Start Date</Label>
                <Input
                  type="date"
                  name="leave_startDate"
                  value={formik.values.leave_startDate}
                  onChange={formik.handleChange}
                />
              </Col>
              <Col md="6">
                <Label>Leave End Date</Label>
                <Input
                  type="date"
                  name="leave_endDate"
                  value={formik.values.leave_endDate}
                  onChange={formik.handleChange}
                />
              </Col>
            </Row>

            <Row className="mt-3">
              <Col md="6">
                <Label>No. of Leave Days</Label>
                <Input type="number" value={days_count} disabled />
              </Col>
              <Col md="6">
                <Label>Status</Label>
                <Input
                  type="select"
                  name="status"
                  value={formik.values.status}
                  onChange={formik.handleChange}
                >
                  <option value="enabled">Enabled</option>
                  <option value="disabled">Disabled</option>
                </Input>
              </Col>
            </Row>

            <Row className="mt-3">
              <Col md="12">
                <Label>Remark</Label>
                <Input
                  type="textarea"
                  name="remark"
                  value={formik.values.remark}
                  onChange={formik.handleChange}
                />
              </Col>
            </Row>

            <ModalFooter>
              <Button color="secondary" onClick={handleClose}>
                Cancel
              </Button>
              <Button color="primary" type="submit">
                Submit
              </Button>
            </ModalFooter>
          </Form>
        </ModalBody>
      </Modal>
    </div>
  )
}

export default LeaveForm
